import { Injectable, EventEmitter, Output, OnDestroy } from '@angular/core'
import { Client, Stomp } from '@stomp/stompjs'
import SockJS from 'sockjs-client'

import { LOCAL_STORAG_KEYS } from '@@constants/local-storage-keys'
import isJson from '@@utils/isJson'

import { GeneralService } from './general.service'
import { AuthService } from './auth.service'
import { FingerPrintService } from './fingerprint.service'

import { environment } from './../../environments/environment'
import { WebSocketConfig } from '@@n2-interfaces/aws-websocket.interface'
import { AwsWebSocketService } from '@@services/aws-websocket.service'
import { BehaviorSubject, Subject } from 'rxjs'
import { take } from 'rxjs/operators'

@Injectable({
    providedIn: 'root'
})
export class WebsocketService implements OnDestroy {
    @Output() compdata: EventEmitter<any> = new EventEmitter();
    @Output() onUserCheckin: EventEmitter<any> = new EventEmitter();
    @Output() onlineBookingStatusEmitter: EventEmitter<any> = new EventEmitter();
    @Output() listenToMyFatoorah: EventEmitter<any> = new EventEmitter();
    @Output() listenToPayzah: EventEmitter<any> = new EventEmitter();
    @Output() listenToPayMob: EventEmitter<any> = new EventEmitter();

    private config!: WebSocketConfig;
    private socket: any;
    private stomp: any;
    private timer: any;
    private resolveConPromise: ((...args: any[]) => void) | undefined;
    private queuePromises: any = [];
    private disconnectPromise: Promise<any>;
    private resolveDisConPromise: ((...args: any[]) => void) | undefined;

    // Connection state management
    public connectionState$: BehaviorSubject<string> = new BehaviorSubject<string>('CLOSED');
    public status: string = 'CLOSED';
    private isConnecting: boolean = false;
    private wsCounter: number = 0;
    private wsSubscriptionList: {[key: string]: any} = {};
    private wsConnected: boolean = false;
    private checkinTopic: string;
    private alreadyListening: boolean = false;

    // Subscription tracking
    private subscriptions: Subject<void> = new Subject<void>();

    // Payment gateway states
    callPaymentGateway = false;
    onlineBookingSession: any;
    callMyFatoorah = false;
    myFatoorahSession: any;
    callPayzah = false;
    payzahSession: any;
    callPayMob = false;
    payMobSession: any;

    private wsSubscriptions = { checkin: false };

    constructor(
        private authService: AuthService,
        private generalService: GeneralService,
        private awsWebSocketService: AwsWebSocketService
    ) {
        this.checkinTopic = "/topic/checkin/" + localStorage.getItem("idSelectedBranch");
        this.disconnectPromise = new Promise((resolve) => this.resolveDisConPromise = resolve);

        // Sync connection state with AWS WebSocket service if using it
        if (environment.useAwsWebSocket) {
            this.awsWebSocketService.status$.subscribe(status => {
                this.status = status;
                this.connectionState$.next(status);
                this.wsConnected = (status === 'CONNECTED');
            });
        } else {
            // Only configure STOMP in non-AWS mode
            this.configure({
                host: environment.socketUrl,
                queue: { 'init': false },
                debug: false,
                headers: {},
                heartbeatIn: 10000,
                heartbeatOut: 10000,
                recTimeout: 5000,
                queueCheckTime: 100
            });
        }

        this.initializeSubscriptions();
    }

    private initializeSubscriptions() {
        this.authService.wsLogout.subscribe(() => {
            this.disconnect();
        });

        AuthService.wsCheckCheckin.subscribe(() => {
            this.userCheckinListener();
        });
    }

    ngOnDestroy(): void {
        // Clean up all subscriptions
        this.subscriptions.next();
        this.subscriptions.complete();

        // Disconnect if connected
        if (this.status === 'CONNECTED') {
            this.disconnect();
        }
    }

    public connect(): void {
        // Prevent multiple concurrent connection attempts
        if (this.isConnecting || this.status === 'CONNECTED') {
            return;
        }

        this.isConnecting = true;

        if (environment.useAwsWebSocket) {
            this.connectAws();
        } else if (this.status === "CLOSED") {
            this.connectStomp();
        } else {
            this.isConnecting = false;
        }
    }

    private connectAws(): void {
        this.connectionState$.next('CONNECTING');
        this.awsWebSocketService.connect();

        // Wait for connection to be established
        this.awsWebSocketService.status$.pipe(take(1)).subscribe(status => {
            if (status === 'CONNECTED') {
                this.handleConnectionCallbacks();
            }
            this.isConnecting = false;
        });
    }

    private connectStomp(): void {
        this.connectionState$.next('CONNECTING');
        this._connect().then(() => {
            this.done('init');
            this.wsConnected = true;
            this.connectionState$.next('CONNECTED');
            console.log("ws:", "Connected");
            this.handleConnectionCallbacks();
            this.isConnecting = false;
        }).catch(error => {
            console.error("ws: Connection failed", error);
            this.connectionState$.next('ERROR');
            this.isConnecting = false;
        });
    }

    private handleConnectionCallbacks(): void {
        if (this.callPaymentGateway) {
            this.callPaymentGateway = false;
            this.onlineBookingStatusListen(this.onlineBookingSession);
        }
        if (this.callMyFatoorah) {
            this.callMyFatoorah = false;
            this.myFatoorahListen(this.myFatoorahSession);
        }
        if (this.callPayzah) {
            this.callPayzah = false;
            this.payzahListen(this.payzahSession);
        }
        if (this.callPayMob) {
            this.callPayMob = false;
            this.paymobListen(this.payMobSession);
        }
    }

    public disconnect(): void {
        if (this.status === "CONNECTED" || this.status === "CONNECTING") {
            this.connectionState$.next('DISCONNECTING');

            if (environment.useAwsWebSocket) {
                this.awsWebSocketService.disconnect();
            } else {
                this._disconnect().then(() => {
                    this.unsubscribe();
                });
            }

            this.wsConnected = false;
            this.alreadyListening = false;
            this.status = 'CLOSED';
            this.connectionState$.next('CLOSED');
        }
    }

    public configure(config: WebSocketConfig): void {
        this.config = config;
    }

    private _connect(): Promise<{}> {
        if (!this.config) throw Error('config missing');

        this.status = 'CONNECTING';
        this.socket = new SockJS(this.config.host);
        this.stomp = Stomp.over(this.socket);

        this.stomp.heartbeat.outgoing = this.config.heartbeatOut || 10000;
        this.stomp.heartbeat.incoming = this.config.heartbeatIn || 10000;
        this.stomp.debug = this.config.debug ?
            (str: any) => console.log("ws:", str) :
            () => {}

        this.stomp.connect(
            this.config.headers || {},
            this.onConnect,
            this.onError
        );

        return new Promise((resolve) => this.resolveConPromise = resolve);
    }

    private _subscribe(destination: string, callback: any, headers?: Object): any {
        console.log("ws: Listening to", destination);
        this.wsSubscriptionList["sub-" + this.wsCounter] = destination;
        this.wsCounter++;

        if (destination === this.checkinTopic) {
            this.alreadyListening = true;
        }

        if (environment.useAwsWebSocket) {
            return this.awsWebSocketService.subscribe(destination)
                .subscribe(data => callback(data));
        } else {
            headers = headers || {};
            return this.stomp.subscribe(destination, (response: any) => {
                const message = JSON.parse(response.body);
                callback(message, response.headers);
            }, headers);
        }
    }

    public send(destination: string, body: any, headers?: Object): void {
        if (environment.useAwsWebSocket) {
            this.awsWebSocketService.send(destination, body);
        } else {
            headers = headers || {};
            this.stomp.send(destination, headers, JSON.stringify(body));
        }
    }

    private onConnect = (frame: any) => {
        this.setListener(this.checkinTopic);
        const wsListener = localStorage.getItem("wsListener");

        if (wsListener && FingerPrintService.isFingerPrintEnabled()) {
            this.userCheckinListenerResub();
        }

        console.log('ws: Connected');
        this.status = 'CONNECTED';
        this.connectionState$.next('CONNECTED');
        this.resolveConPromise?.();
        this.timer = null;
    }

    private onError = (error: string) => {
        console.error("ws: Error", error);
        this.connectionState$.next('ERROR');

        if (error.indexOf('Lost connection') !== -1) {
            this.alreadyListening = false;
            console.log('ws: Reconnecting...');
            this.connectionState$.next('RECONNECTING');

            // Only attempt reconnection if no active connection attempt
            if (!this.isConnecting) {
                this.isConnecting = true;
                this.timer = setTimeout(
                    () => {
                        this._connect().finally(() => {
                            this.isConnecting = false;
                        });
                    },
                    this.config.recTimeout || 5000
                );
            }
        }
    }

    public unsubscribe(): void {
        if (!environment.useAwsWebSocket && this.stomp) {
            for (const subscription in this.stomp.subscriptions) {
                if (this.stomp.subscriptions.hasOwnProperty(subscription) && subscription !== 'sub-0') {
                    console.log("ws: Removed", this.wsSubscriptionList[subscription]);
                    this.stomp.unsubscribe(subscription);
                }
            }
        }
    }

    private _disconnect(): Promise<{}> {
        this.stomp.disconnect(() => {
            this.resolveDisConPromise?.();
            this.status = 'CLOSED';
        });
        return this.disconnectPromise;
    }

    // Queue Management
    public done(name: string): void {
        this.nameCheck(name);
        this.config.queue[name] = true;
    }

    public pending(name: string): void {
        this.nameCheck(name);
        this.config.queue[name] = false;
    }

    private nameCheck(name: string): void {
        if (!this.config.queue.hasOwnProperty(name)) {
            throw Error(`ws: '${name}' has not found in queue`);
        }
    }

    public after(name: string): Promise<{}> {
        this.nameCheck(name);
        if (this.config.debug) console.log('ws: checking ' + name + ' queue ...');

        let checkQueue = setInterval(() => {
            if (this.config.queue[name]) {
                clearInterval(checkQueue);
                this.queuePromises[name]();
                if (this.config.debug) console.log('ws: queue ' + name + ' <- has been completed');
                return false;
            }
            return;
        }, this.config.queueCheckTime || 100);

        if (!this.queuePromises[name + 'promise']) {
            this.queuePromises[name + 'promise'] = new Promise((resolve) => this.queuePromises[name] = resolve);
        }
        return this.queuePromises[name + 'promise'];
    }

    // Listener Management
    public setListener(message: string): void {
        localStorage.setItem("wsListener", message);
    }

    public userCheckinListener(): void {
        if (this.wsSubscriptions.checkin) return;

        // Check if already connected
        if (this.status === 'CONNECTED') {
            this.subscribeToCheckin();
            return;
        }

        // Subscribe once connected
        this.connectionState$.pipe(take(1)).subscribe(state => {
            if (state === 'CONNECTED') {
                this.subscribeToCheckin();
            } else {
                if (environment.useAwsWebSocket) {
                    this.connectAws();
                    this.awsWebSocketService.status$.subscribe(status => {
                        if (status === 'CONNECTED') {
                            this.subscribeToCheckin();
                        }
                    });
                } else {
                    this._connect().then(() => {
                        this.done('init');
                        this.wsConnected = true;
                        this.subscribeToCheckin();
                    });
                }
            }
        });
    }

    private subscribeToCheckin(): void {
        const message = "/topic/checkin/" + localStorage.getItem("idSelectedBranch");
        if (!this.alreadyListening) {
            this._subscribe(message, (data: any) => {
                if (data) {
                    this.setListener(message);
                    data = isJson(data) ? data : JSON.parse(data);
                    data.user = data.userName || false;
                    data.statustemp = data.message;
                    data.message = this.statusMessage(
                        data.status || data.statustemp,
                        data.user,
                        data.type
                    );
                    this.onUserCheckin.emit();
                    document.dispatchEvent(
                        new CustomEvent('ws-member-notification', { detail: { data } })
                    );
                }
            });
        }
    }

    public userCheckinListenerResub(): void {
        const message = "/topic/checkin/" + localStorage.getItem("idSelectedBranch");
        if (!this.alreadyListening) {
            this._subscribe(message, this.handleCheckinData.bind(this));
        }
    }

    private handleCheckinData(data: any): void {
        if (!data) return;

        const message = "/topic/checkin/" + localStorage.getItem("idSelectedBranch");
        this.setListener(message);

        data = isJson(data) ? data : JSON.parse(data);
        data.user = data.userName || false;
        data.statustemp = data.message;
        data.message = this.statusMessage(
            data.status || data.statustemp,
            data.user,
            data.type
        );

        this.onUserCheckin.emit();
        document.dispatchEvent(
            new CustomEvent('ws-member-notification', { detail: { data } })
        );
    }

    public userEnroll(user: any): void {
        if (!this.wsConnected) {
            this.connect();
        }

        const message = `/topic/enroll/${user.userType}/${user.id}`;
        this._subscribe(message, (data: any) => {
            if (data) {
                data = isJson(data) ? data : JSON.parse(data);
                if (data.message === "Enrollment-Complete") {
                    console.log("finished");
                }
                this.compdata.emit(data);
            }
        });
    }

    // Payment Gateway Listeners
    public onlineBookingStatusListen(checkout: any): void {
        if (environment.useAwsWebSocket && this.awsWebSocketService.status$.value === 'CONNECTED') {
            this.wsConnected = true;
        }

        if (!this.wsConnected) {
            this.callPaymentGateway = true;
            this.onlineBookingSession = checkout;
            this.connect();
        } else {
            const message = `/topic/onlineBooking/${checkout.sessionId}`;
            console.log(`Subscribing to online booking status: ${message}`);

            if (environment.useAwsWebSocket) {
                // For AWS WebSocket, we need to ensure the connection is ready
                if (this.awsWebSocketService.status$.value !== 'CONNECTED') {
                    console.log('AWS WebSocket not connected, connecting first...');
                    // Store for reconnection
                    this.callPaymentGateway = true;
                    this.onlineBookingSession = checkout;
                    this.awsWebSocketService.connect();
                } else {
                    console.log('AWS WebSocket connected, subscribing directly');
                    this._subscribe(message, (data: any) => {
                        console.log('Received online booking status update:', data);
                        this.handlePaymentResponse(this.onlineBookingStatusEmitter, data);
                    });
                }
            } else {
                // Original STOMP implementation
                this._subscribe(message, this.handlePaymentResponse.bind(this, this.onlineBookingStatusEmitter));
            }
        }
    }

    public myFatoorahListen(checkout: any): void {
        if (environment.useAwsWebSocket && this.awsWebSocketService.status$.value === 'CONNECTED') {
            this.wsConnected = true;
        }

        if (!this.wsConnected) {
            this.callMyFatoorah = true;
            this.myFatoorahSession = checkout;
            this.connect();
        } else {
            const message = `/topic/onlineBooking/myfatoorah/${checkout.sessionId}`;
            console.log(`Subscribing to MyFatoorah status: ${message}`);

            if (environment.useAwsWebSocket) {
                // For AWS WebSocket, we need to ensure the connection is ready
                if (this.awsWebSocketService.status$.value !== 'CONNECTED') {
                    console.log('AWS WebSocket not connected, connecting first...');
                    this.awsWebSocketService.connect();
                    // Store for reconnection
                    this.callMyFatoorah = true;
                    this.myFatoorahSession = checkout;
                } else {
                    console.log('AWS WebSocket connected, subscribing directly');
                    this._subscribe(message, (data: any) => {
                        console.log('Received MyFatoorah status update:', data);
                        this.handlePaymentResponse(this.listenToMyFatoorah, data);
                    });
                }
            } else {
                // Original STOMP implementation
                this._subscribe(message, this.handlePaymentResponse.bind(this, this.listenToMyFatoorah));
            }
        }
    }

    public payzahListen(checkout: any): void {
        if (environment.useAwsWebSocket && this.awsWebSocketService.status$.value === 'CONNECTED') {
            this.wsConnected = true;
        }

        if (!this.wsConnected) {
            this.callPayzah = true;
            this.payzahSession = checkout;
            this.connect();
        } else {
            const message = `/topic/onlineBooking/payzah/${checkout.sessionId}`;
            console.log(`Subscribing to Payzah status: ${message}`);

            if (environment.useAwsWebSocket) {
                // For AWS WebSocket, we need to ensure the connection is ready
                if (this.awsWebSocketService.status$.value !== 'CONNECTED') {
                    console.log('AWS WebSocket not connected, connecting first...');
                    this.awsWebSocketService.connect();
                    // Store for reconnection
                    this.callPayzah = true;
                    this.payzahSession = checkout;
                } else {
                    console.log('AWS WebSocket connected, subscribing directly');
                    this._subscribe(message, (data: any) => {
                        console.log('Received Payzah status update:', data);
                        this.handlePaymentResponse(this.listenToPayzah, data);
                    });
                }
            } else {
                // Original STOMP implementation
                this._subscribe(message, this.handlePaymentResponse.bind(this, this.listenToPayzah));
            }
        }
    }

    public paymobListen(checkout: any): void {
        if (environment.useAwsWebSocket && this.awsWebSocketService.status$.value === 'CONNECTED') {
            this.wsConnected = true;
        }

        if (!this.wsConnected) {
            this.callPayMob = true;
            this.payMobSession = checkout;
            this.connect();
        } else {
            const message = `/topic/onlineBooking/paymob/${checkout.session_id}`;

            if (environment.useAwsWebSocket) {
                // For AWS WebSocket, we need to ensure the connection is ready
                if (this.awsWebSocketService.status$.value !== 'CONNECTED') {
                    this.awsWebSocketService.connect();
                    // Store for reconnection
                    this.callPayMob = true;
                    this.payMobSession = checkout;
                } else {
                    this._subscribe(message, (data: any) => {
                        this.handlePaymentResponse(this.listenToPayMob, data);
                    });
                }
            } else {
                // Original STOMP implementation
                this._subscribe(message, this.handlePaymentResponse.bind(this, this.listenToPayMob));
            }
        }
    }

    private handlePaymentResponse(emitter: EventEmitter<any>, data: any): void {
        if (!data) return;

        data = isJson(data) ? data : JSON.parse(data);
        if (data.message === "Success") {
            console.log("finished");
        }
        emitter.emit(data);
    }

    // Status Message Generation
    private statusMessage(status: string, user?: string, type?: string): string {
        const typeTransform: { [key: string]: string } = {
            "checkin": "checked-in",
            "checkout": "checked-out"
        };

        const username = user || "User";
        type = type || "Access";

        const messages: {[key: string]: string} = {
            "authorizedAccess": `${username} has ${typeTransform[type]} successfully.`,
            "clientDeleted": `Client ${username} deleted, trying to ${type}.`,
            "clientNoLongerInSystem": `User not in the system, trying to ${type}.`,
            "staffTerminated": `The staff ${username} is no longer a staff, trying to ${type}.`,
            "staffNoLongerInSystem": `The staff ${username} is not in the system, trying to ${type}.`,
            "unidentifiedAccess": `Unidentified user is trying to ${type}.`,
            "invalidMembership": `Client with invalid membership, trying to ${type}.`,
            "openCheckin": `${username} is trying to checkin but did not checkout first.`,
            "noOpenCheckin": `${username} is trying to checkout but did not checkin first.`
        };

        return messages[status] || status;
    }
}
